import { useMemo, useState, useEffect } from 'react';

import { useBreakpointValue } from '@vimeo/bokeh';
import { useGetAlbumVideos } from '@vimeo/gctl-api/hooks/useAlbumVideos';
import { useGetAlbumVideosInfinite } from '@vimeo/gctl-api/hooks/useAlbumVideos';
import { getInfiniteRequestLoadingState } from '@vimeo/ui/libs/getInfiniteRequestLoadingState';
import { useViewer } from '@vimeo/viewer';
import { useBaseUrl } from '@vimeo/viewer-showcase-ui/hooks/use-base-url';
import { DEFAULT_SHOWCASE_PLAYER_EMBED_OVERRIDE_QUERY } from '@vimeo/viewer-showcase-ui/utils/constants';
import { getEventIdFromClip } from '@vimeo/viewer-showcase-ui/utils/helper';

import { useAlbumStore } from '../state/albumStore';
import {
  DEFAULT_SHOWCASE_PLAYER_RESPONSIVE_QUERY,
  ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_MOBILE,
  SELECT_ALBUM_FIELDS,
  VIDEOS_API_VERSION,
} from '../utils/constants';
import { contextUrlParser } from '../utils/helper';
import { useGetDeeplinkedVideoPage } from './use-get-deeplinked-video-page';
import { useGetShowcase } from './use-get-showcase';

const DEFAULT_SHOWCASE_QUERY = {
  forceCollectionUri: true,
  sizes: ['640x360', '360x203'],
};

export const useGetInfiniteVideos = () => {
  const viewer = useViewer();

  /**
   * Handle page url can have different pathname from resource links
   * e.g embedded showcase's pathname is `/showcase/11580063/embed2`
   * but the resource link is `/showcase/11580063`
   */
  const baseUrl = useBaseUrl(); // Use the custom hook for baseUrl

  const { albumId, password, isShowcaseEmbedded, referrer } = useAlbumStore((state) => ({
    albumId: state.albumId,
    password: state.hashedPass,
    isShowcaseEmbedded: state.isShowcaseEmbedded,
    referrer: state.referrer,
  }));

  const embedQuery = {
    isEmbed: isShowcaseEmbedded,
    referrer,
  };

  /** Additional query params to send along API request */
  const { sortConfig, hideUpcoming } = useGetShowcase();

  const perPage = useBreakpointValue(
    { base: ITEMS_PER_PAGE_MOBILE, lg: ITEMS_PER_PAGE },
    { ssr: false },
  );

  const {
    data: albumVideos,
    size,
    setSize,
    isLoading,
    ...gctlConfig
  } = useGetAlbumVideosInfinite(
    () => {
      if (!viewer) return null;

      return {
        where: { albumId },
        select: SELECT_ALBUM_FIELDS,
        query: {
          perPage,
          ...DEFAULT_SHOWCASE_QUERY,
          ...DEFAULT_SHOWCASE_PLAYER_EMBED_OVERRIDE_QUERY,
          ...DEFAULT_SHOWCASE_PLAYER_RESPONSIVE_QUERY,
          ...embedQuery,
          ...sortConfig,
          ...(password && { password }),
          ...(hideUpcoming && { filter: 'playable' }),
        },
        headers: { Accept: `application/vnd.vimeo.*+json;version=${VIDEOS_API_VERSION}` },
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  const loadMoreClips = () => {
    setSize(size + 1);
  };

  const { isLoadingInitialData, isLoadingMore, isDone } = getInfiniteRequestLoadingState({
    data: albumVideos,
    size,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const canLoadMoreClips = !isDone && !isLoading;

  const clips = useMemo(
    () =>
      albumVideos
        ?.filter(Boolean)
        ?.flatMap((page) => page.data)
        ?.map((clip) => {
          /**
           * Extract EventId for "archived" live clips
           *
           * GCTL GET Album hook return clipResponse, all clips, including Live Clips.
           *
           * Live clip is a essentially a clip, but it has an event "container" attached to it.
           * Here we gonna use the url with `?event=:eventId` to navigate to this event's live clip in the showcase.
           * Under the hood, player rendering still need to use the (live)clipId, so does video details data and other clip level data.
           *
           * Currently we don't need the event hash here, YET.
           */
          const eventId = getEventIdFromClip(clip); // live clip finished archive (transcoding) process

          return {
            ...clip,
            ...contextUrlParser(clip.link, { baseUrl, eventId }),
            ...(eventId && { eventId }),
          };
        }),
    [albumVideos, baseUrl],
  );

  return {
    clips,
    total: albumVideos?.[0]?.total ?? 0,
    loadMoreClips,
    isLoadingMoreClips: isLoadingMore,
    canLoadMoreClips,
    isLoading: isLoading || isLoadingInitialData,
    isShowcaseEmpty: size === 1 && albumVideos?.[0]?.data?.length === 0,
    ...gctlConfig,
  };
};

export const useGetPaginatedVideos = () => {
  const [total, setTotal] = useState<number>(0);
  const viewer = useViewer();

  useGetDeeplinkedVideoPage(); // Will set clipPlaylistPage in album store required to kick of useGetAlbumVideos call

  /**
   * Handle page url can have different pathname from resource links
   * e.g embedded showcase's pathname is `/showcase/11580063/embed2`
   * but the resource link is `/showcase/11580063`
   */
  const baseUrl = useBaseUrl(); // Use the custom hook for baseUrl

  const { albumId, password, isShowcaseEmbedded, clipPlaylistPage, setClipPlaylistPage, referrer } =
    useAlbumStore((state) => ({
      albumId: state.albumId,
      password: state.hashedPass,
      isShowcaseEmbedded: state.isShowcaseEmbedded,
      clipPlaylistPage: state.clipPlaylistPage,
      setClipPlaylistPage: state.setClipPlaylistPage,
      referrer: state.referrer,
    }));

  const embedQuery = {
    isEmbed: isShowcaseEmbedded,
    referrer,
  };

  /** Additional query params to send along API request */
  const { sortConfig, hideUpcoming } = useGetShowcase();

  const perPage = useBreakpointValue(
    { base: ITEMS_PER_PAGE_MOBILE, lg: ITEMS_PER_PAGE },
    { ssr: false },
  );

  const {
    data: albumVideos,
    isLoading,
    ...gctlConfig
  } = useGetAlbumVideos(
    () => {
      if (!viewer || !clipPlaylistPage) return null;

      return {
        where: { albumId },
        select: SELECT_ALBUM_FIELDS,
        query: {
          perPage,
          page: clipPlaylistPage,
          ...DEFAULT_SHOWCASE_QUERY,
          ...DEFAULT_SHOWCASE_PLAYER_EMBED_OVERRIDE_QUERY,
          ...DEFAULT_SHOWCASE_PLAYER_RESPONSIVE_QUERY,
          ...embedQuery,
          ...sortConfig,
          ...(password && { password }),
          ...(hideUpcoming && { filter: 'playable' }),
        },
        headers: { Accept: `application/vnd.vimeo.*+json;version=${VIDEOS_API_VERSION}` },
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  const clips = albumVideos?.data?.map((clip) => {
    /**
     * Extract EventId for "archived" live clips
     *
     * GCTL GET Album hook return clipResponse, all clips, including Live Clips.
     *
     * Live clip is a essentially a clip, but it has an event "container" attached to it.
     * Here we gonna use the url with `?event=:eventId` to navigate to this event's live clip in the showcase.
     * Under the hood, player rendering still need to use the (live)clipId, so does video details data and other clip level data.
     *
     * Currently we don't need the event hash here, YET.
     */
    const eventId = getEventIdFromClip(clip); // live clip finished archive (transcoding) process

    return {
      ...clip,
      ...contextUrlParser(clip.link, { baseUrl, eventId }),
      ...(eventId && { eventId }),
    };
  });

  useEffect(() => {
    if (albumVideos?.total) {
      setTotal(albumVideos?.total);
    }
  }, [albumVideos?.total]);

  return {
    clips,
    total,
    page: clipPlaylistPage,
    perPage: albumVideos?.perPage,
    setPage: setClipPlaylistPage,
    isLoading: isLoading,
    ...gctlConfig,
  };
};
